import { useEffect, useState, useRef } from "react";
import { fetcher } from "../../services/api";
import ChevronDownIcon from "../../assets/icons/chveron-down";
import Loader from "../Loader/Loader";
import "./FAQ.css";

interface FaqItem {
  question: string;
  answer: string;
}

interface FaqResponse {
  result: boolean;
  data: FaqItem[];
}

export default function FaqAccordion() {
  const [faqData, setFaqData] = useState<FaqItem[]>([]);
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const answerRefs = useRef<(HTMLParagraphElement | null)[]>([]);

  const initialHash = useRef<string | null>(null);
  useEffect(() => {
    initialHash.current = window.location.hash;
  }, []);

  useEffect(() => {
    const fetchFaq = async () => {
      try {
        const res = await fetcher<FaqResponse>("/publisher/faq", {
          method: "GET",
        });

        const faqArray = res?.data?.data;
        if (Array.isArray(faqArray)) {
          setFaqData(faqArray);
        } else {
          console.error("FAQ data is not an array:", res.data);
          setFaqData([]);
        }
      } catch (error) {
        console.error("Failed to fetch FAQ data:", error);
        setFaqData([]);
      }
    };

    fetchFaq();
  }, []);

  useEffect(() => {
    if (faqData.length === 0 || !initialHash.current) return;

    const match = initialHash.current.match(/#(\d+)/);
    const index = match ? parseInt(match[1], 10) : null;

    if (index !== null && index >= 0 && index < faqData.length) {
      setOpenIndex(index);
      setTimeout(() => {
        const el = document.getElementById(`${index}`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
    }
  }, [faqData]);

  const toggle = (index: number) => {
    const isOpen = index === openIndex;
    setOpenIndex(isOpen ? null : index);
    window.history.replaceState(
      null,
      "",
      isOpen ? window.location.pathname : `#${index}`
    );
  };

  return (
    <div className="faq-container main-container">
      <h2>Frequently Asked Questions</h2>
      <ul>
        {Array.isArray(faqData) && faqData.length > 0 ? (
          faqData.map((item, index) => (
            <li
              key={index}
              id={`${index}`}
              className={`faq-item ${openIndex === index ? "open" : ""}`}
              onClick={() => toggle(index)}
            >
              <div className="faq-question">
                <span>{item.question}</span>
                <ChevronDownIcon
                  className={`chevron ${openIndex === index ? "rotate" : ""}`}
                />
              </div>

              <div
                className="faq-answer-wrapper"
                style={{
                  height:
                    openIndex === index
                      ? `${answerRefs.current[index]?.scrollHeight}px`
                      : "0px",
                }}
              >
                <p
                  ref={(el) => {
                    answerRefs.current[index] = el;
                  }}
                  className="faq-answer"
                  dangerouslySetInnerHTML={{ __html: item.answer }}
                />
              </div>
            </li>
          ))
        ) : (
          <Loader />
        )}
      </ul>
    </div>
  );
}
